import React, { ForwardRefRenderFunction, useEffect, useState } from "react";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import { EXCEPT_BUILDING, NEAR_DISTANCE } from "../../types/building";

import useErrors from "../../libs/useErrors";

import {
  BuildingFloorItem,
  BuildingSpaceItem,
  NearBuildingItem,
  getBuildingFloorList,
  getBuildingSpaceList,
  getDistanceWithBuilding,
  getNearBuilding,
  getNearVocBuilding,
} from "../../services/buildingServices";
import { logout } from "../../services/authServices";

import { useUserContext } from "../../contexts/userContext";
import { Coordinates, Reason, defaultCoordinates } from "../../types/location";

import { useDeviceType } from "../../hooks/useDeviceType/Index";
import useGPS from "../../hooks/useGPS";
import useFullScreenLoading from "../../hooks/useFullScreenLoading";
import useSimplePopup from "../../hooks/useSimplePopup";

import FindBuilding, { BuildingLocationItem } from "../Building/Find";
import CommonButton from "../Button";
import CommonSelect from "../Select";
import CommonPopup, { CommonPopupProps } from "../Popup";

import ZoomSearchIcon from "../../svgs/icons/ZoomSearchIcon";
import BuildingInfo from "../../pages/View/BuildingInfo";

type Props = {
  selectedBuilding?: NearBuildingItem;
  selectedFloor?: BuildingFloorItem;
  selectedSpace?: BuildingSpaceItem;
  onClickBuilding?: () => void;
  onChangeBuilding?: (building?: NearBuildingItem) => void;
  onClickFloor?: () => void;
  onChangeFloor?: (floor?: BuildingFloorItem) => void;
  onClickSpace?: () => void;
  onChangeSpace?: (space?: BuildingSpaceItem) => void;
  buildingError?: string;
  floorError?: string;
  spaceError?: string;
  unUseRecommend?: boolean;
  buildingRef?: React.Ref<HTMLInputElement>;
  floorRef?: React.Ref<HTMLInputElement>;
  spaceRef?: React.Ref<HTMLInputElement>;
  showSpace?: boolean;
  onLoaded?: () => void;
  onBackHistory?: () => void;
  reason: Reason
  isVoc?: boolean
};

const BuildingFloorSelector: ForwardRefRenderFunction<
  HTMLInputElement[],
  Props
> = (
  {
    selectedBuilding,
    selectedFloor,
    selectedSpace,
    onClickBuilding,
    onChangeBuilding,
    onClickFloor,
    onChangeFloor,
    onClickSpace,
    onChangeSpace,
    buildingError,
    floorError,
    spaceError,
    buildingRef,
    floorRef,
    spaceRef,
    onBackHistory,
    onLoaded,
    showSpace = false,
    unUseRecommend = false,
    reason,
    isVoc = false
  },
  refs
) => {
  const deviceType = useDeviceType();
  const [locationResult, callLocation] = useGPS({
    isDesktop: deviceType === "unknown",
  });
  const [userCoordinates, setUserCoordinates] = useState<
    Coordinates | undefined
  >(process.env.NODE_ENV === "development" ? defaultCoordinates : undefined);
  const [popup, setPopup] = useState<"select_building" | "">("");
  const [isSoFar, setIsSoFar] = useState<boolean | undefined>();
  const [openVoc, setOpenVoc] = useState<boolean>(false);

  const [recommendBuilding, setRecommendBuilding] = useState<NearBuildingItem>();
  const [vocRecommendBuilding, setVocRecommendBuilding] = useState<NearBuildingItem>();

  const [floorList, setFloorList] = useState<BuildingFloorItem[]>([]);
  const [spaceList, setSpaceList] = useState<BuildingSpaceItem[]>([]);
  const [user] = useUserContext();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFloorLoading, setIsFloorLoading] = useState<boolean>(true);
  const [isSpaceLoading, setIsSpaceLoading] = useState<boolean>(true);
  const [isLocationLoading, setIsLocationLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({
    isLoading: isLoading || isLocationLoading,
  });
  const [errors, { setError, removeError }] = useErrors();
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const popupInfo: { [key: string]: CommonPopupProps } = {
    select_building: {
      title: "건물 선택",
      children: (
        <FindBuilding
          defaultBuilding={selectedBuilding}
          onChange={(value: NearBuildingItem) => {
            onChangeBuilding?.(value);
            onChangeFloor?.(undefined);
            onChangeSpace?.(undefined);
          }}
          onClose={() => setPopup("")}
          coordinate={userCoordinates}
          onCallback={callLocation}
          reason={reason}
        />
      ),
      unUseHorizonRule: true,
    },
  };

  useEffect(() => {
    if (selectedBuilding?.buildingId) {
      if (isVoc) {
        setVocRecommendBuilding(selectedBuilding);
      }
      setIsFloorLoading(true);
      getBuildingFloorList({ buildingId: selectedBuilding.buildingId })
        .then((res) => {
          setFloorList(res.data);
        })
        .finally(() => {
          setIsFloorLoading(false);
        });
    }
  }, [selectedBuilding, isVoc]);

  useEffect(() => {
    if (
      showSpace &&
      selectedBuilding?.buildingId &&
      selectedFloor?.buildingFloorId
    ) {
      setIsSpaceLoading(true);
      getBuildingSpaceList({
        buildingId: selectedBuilding.buildingId,
        buildingFloorId: selectedFloor.buildingFloorId,
      })
        .then((res) => {
          setSpaceList(res.data);
        })
        .finally(() => {
          setIsSpaceLoading(false);
        });
    }
  }, [selectedFloor, selectedBuilding, showSpace]);

  useEffect(() => {

    if (userCoordinates === undefined || isSoFar === undefined) return;

    if (!isSoFar) {
      try {
        getNearBuilding({
          ...userCoordinates,
          page: 1,
          pageSize: 2,
          reason
        }).then((res) => {
          const result = res.data.items?.filter(
            (item) =>
              item.buildingId !== user.buildingId &&
              item.distance <= NEAR_DISTANCE
          );
          if (result?.length > 0) {
            setRecommendBuilding(res.data.items[0]);
          } else {
            setRecommendBuilding(undefined);
          }

        });
      } catch (e) {
        setRecommendBuilding(undefined);
      }

      if(isVoc) {
        updateDefaultBuildingInfo();
      }
    } else if (isVoc) {
      setVocRecommendBuilding({
        atgBuildingId: user?.buildingId ?? "",
        buildingId: user?.buildingId ?? "",
        buildingName: user?.buildingName ?? "",
        address: "",
        distance: 0,
        lat: 0,
        lng: 0,
        atgBuildingVocId: user?.buildingId ?? ""
      });
    } 

  }, [isSoFar, userCoordinates]);

  const updateDefaultBuildingInfo = () => {
    onChangeBuilding?.({
      buildingId: user?.buildingId ?? "",
      buildingName: user?.buildingName ?? "",
    } as NearBuildingItem);
    onChangeFloor?.({
      buildingFloorId: user?.buildingFloorId ?? "",
      floorName: user?.buildingFloorName ?? "",
    } as BuildingFloorItem);
    onChangeSpace?.({
      buildingSpaceId: user?.buildingSpaceId ?? "",
      spaceName: user?.buildingSpaceName ?? "",
    } as BuildingSpaceItem);
  };

  const getIsSoFar = async () => {
    if (user.buildingId && EXCEPT_BUILDING.includes(user.buildingId)) {
      return setIsSoFar(false);
    }

    try {
      const result = await getDistanceWithBuilding({
        buildingId: user.buildingId ?? "",
        ...userCoordinates,
        reason
      });
      if (result.status === HttpStatusCode.Ok) {
        setIsSoFar(!result.data.result);

        if(isVoc) {
          setOpenVoc(!result.data.result);
        }
      } else {        
        setIsSoFar(true);
      }
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout();
      } else if (e?.response?.data?.message) {
        open(e.response.data.message?.replace(". ", ".\n"));
      } else {
        open("근처 건물 체크 실패");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectBuilding = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    onClickBuilding?.();
    removeError("building");
    callLocation();
    setPopup("select_building");
  };

  useEffect(() => {
    if (
      user.isLoading === false &&
      isLocationLoading === false &&
      userCoordinates &&
      isSoFar === undefined
    ) {
      setIsLoading(true);
      if (user.buildingId && userCoordinates) {
        getIsSoFar();
      } else {
        setIsSoFar(true);
      }
      setIsLoading(false);
    }
  }, [user.isLoading, user.buildingId, isLocationLoading, userCoordinates]);

  useEffect(() => {
    if (locationResult.isLoading === false && locationResult.result) {
      setIsLocationLoading(false);
      if (locationResult.result.error) {
        if (locationResult.result.error === "denied") {
          onBackHistory?.();
        } else {
          open(
            locationResult.result.errorMessage ??
              "위치정보를 가져오는데 실패했습니다.",
            {
              onClick: () => {
                setIsLocationLoading(true);
                callLocation();
              },
            }
          );
        }
        return;
      } else {
        setUserCoordinates(locationResult.result);
      }
    }
  }, [locationResult]);

  useEffect(() => {
    removeError("building");
    removeError("floor");
    removeError("space");
    if (buildingError) {
      setError("building", buildingError);
    }
    if (floorError) {
      setError("floor", floorError);
    }
    if (spaceError) {
      setError("space", spaceError);
    }
  }, [buildingError, floorError, spaceError]);

  useEffect(() => {
    if (isLoading === false && isLocationLoading === false) {
      onLoaded?.();
    }
  }, [isLoading, isLocationLoading]);

  useEffect(() => {
    setIsLocationLoading(true);
    callLocation();
  }, []);

  const getBuildingNameDisplay = () => {
    if (isVoc) {
      if (isSoFar) {
        return vocRecommendBuilding?.buildingName;
      } else {
        return selectedBuilding?.buildingName ?? "건물 선택";
      }
    } else {
      return selectedBuilding?.buildingName ?? "건물 선택";
    }

  };

  return (
    <div className="flex flex-col gap-3">
      {loadingComponent}
      <div onClick={!openVoc ? handleSelectBuilding : undefined}>
        <div
          className={clsx([
            "relative border focus:outline-none p-4 w-full rounded-[3px] h-bottom-button design_min:placeholder:text-sm border-none bg-brand-neutral-05 text-brand-neutral-60",
            {
              "text-opacity-70 text-sm": !selectedBuilding?.buildingName,
              "text-base": selectedBuilding?.buildingName,
            },
          ])}
        >
          {getBuildingNameDisplay()}
          <div className="absolute right-1 top-1/2 -translate-y-1/2 w-fit h-fit px-3 opacity-60">
            <ZoomSearchIcon />
          </div>
        </div>
      </div>
      {!isVoc && !unUseRecommend && isSoFar && !selectedBuilding?.buildingName && (
        <div className="relative">
          <div className="absolute w-full px-2">
            <div className="relative bg-white py-4 px-4 rounded-lg border z-10">
              <div className="absolute left-0 ml-3 -top-[0.52px] w-3 h-3 transform translate-x-1/2 -translate-y-1/2 rotate-45 bg-white border-t border-l"></div>

              <div className="whitespace-pre-line pb-2.5 font-semibold">
                {recommendBuilding && "기본 건물이 현위치와 멀어요\n가장 가까운 건물을 보여드릴게요!"}
              </div>              
              {recommendBuilding && (
                <>
                  <BuildingLocationItem
                    item={recommendBuilding}
                    onClick={() => {
                      removeError("building");
                      onClickBuilding?.();
                      onChangeBuilding?.(recommendBuilding);
                      onChangeFloor?.(undefined);
                      onChangeSpace?.(undefined);
                    }}
                    radioValue={undefined}
                    className="px-0"
                  />
                  <CommonButton
                    className="w-full h-bottom-button"
                    variant="grey"
                    onClick={() => {
                      removeError("building");
                      onClickBuilding?.();
                      onChangeBuilding?.(recommendBuilding);
                      onChangeFloor?.(undefined);
                      onChangeSpace?.(undefined);
                    }}
                  >
                    이 건물로 변경할게요
                  </CommonButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isVoc && openVoc && (
        <div className="relative">
          <div className="absolute w-full px-2">
            <div className="relative bg-white py-4 px-4 rounded-lg border z-10">
              <div className="absolute left-0 ml-3 -top-[0.52px] w-3 h-3 transform translate-x-1/2 -translate-y-1/2 rotate-45 bg-white border-t border-l"></div>
              <div className="whitespace-pre-line pb-2.5 font-semibold">
                현재 근무 중인 건물이 맞는지 확인해 주세요!
              </div>
              <div className="flex gap-2 mt-3">
                <CommonButton
                  className="w-1/2 h-bottom-button"
                  variant="grey"
                  onClick={() => {
                    removeError("building");
                    onChangeBuilding?.(vocRecommendBuilding);
                    onClickBuilding?.();
                    setOpenVoc(false);
                  }}
                >
                  네! 맞아요
                </CommonButton>
                <CommonButton
                  className="w-1/2 h-bottom-button"
                  variant="grey"
                  onClick={() => {
                    removeError("building");
                    onClickBuilding?.();
                    setPopup("select_building");
                    setOpenVoc(false);
                  }}
                >
                  아니에요
                </CommonButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <CommonSelect
        value={selectedFloor?.buildingFloorId ?? ""}
        optionList={floorList
          .sort((a, b) => {
            return a.sortOrder < b.sortOrder ? -1 : 1;
          })
          .map((floor) => ({
            label: floor.floorName,
            value: floor.buildingFloorId,
          }))}
        onClick={() => {
          removeError("floor");
          onClickFloor?.();
        }}
        onChange={(selectedItem) => {
          removeError("floor");
          onClickFloor?.();
          onChangeFloor?.(
            floorList.find(
              (item) => item.buildingFloorId === selectedItem.value
            )
          );
          onChangeSpace?.(undefined);
        }}
        defaultValue={
          !floorList?.length && floorList.length === 0
            ? "건물에 등록된 층이 없습니다."
            : "층 선택"
        }
        disabled={selectedBuilding === undefined || openVoc}
        isLoading={
          selectedBuilding !== undefined &&
          selectedBuilding.buildingId?.length !== undefined &&
          selectedBuilding.buildingId.length > 0 &&
          isFloorLoading
        }
        className="h-[50px] rounded-[3px] px-4"
        error={errors["floor"]}
        ref={floorRef}
        optionOrder="between"
        unUsePadding
      />

      {showSpace && (
        <CommonSelect
          value={selectedSpace?.buildingSpaceId ?? ""}
          optionList={spaceList.map((space) => ({
            label: space.spaceName,
            value: space.buildingSpaceId,
          }))}
          onClick={() => {
            removeError("space");
            onClickSpace?.();
          }}
          onChange={(selectedItem) => {
            removeError("space");
            onClickSpace?.();
            onChangeSpace?.(
              spaceList.find(
                (item) => item.buildingSpaceId === selectedItem.value
              )
            );
          }}
          defaultValue={
            !spaceList?.length && spaceList.length === 0
              ? "등록된 상세공간이 없습니다."
              : "상세공간 선택"
          }
          disabled={selectedFloor === undefined}
          isLoading={
            selectedFloor !== undefined &&
            selectedFloor.buildingFloorId?.length !== undefined &&
            selectedFloor.buildingFloorId.length > 0 &&
            isSpaceLoading
          }
          className="h-[50px] rounded-[3px] px-4"
          error={errors["space"]}
          ref={spaceRef}
          optionOrder="between"
          unUsePadding
        />
      )}

      <CommonPopup
        isOpen={popup !== ""}
        onClose={() => setPopup("")}
        padding={0}
        {...popupInfo[popup]}
      />
      {simplePopupComponent}
    </div>
  );
};

export default React.forwardRef(BuildingFloorSelector);
